import React, { useEffect } from 'react'

import styled from '@emotion/styled'
import { Box } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import {
  type FlexPortionTrackingContext,
  FlexPortionFooter as SharedFlexPortionFooter,
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type { Recipe } from '@library/menu-service'

import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { AddRecipeButton } from 'routes/Menu/components/Detail/AddRecipeButton'
import { useBasket } from 'routes/Menu/domains/basket'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketMenuUuid } from 'selectors/basket'

const ModalFooterContent = styled(Box)({
  margin: '-16px',
  width: 'calc(100% + 32px)',

  '@media (min-width: 640px)': {
    margin: '-24px',
    width: 'calc(100% + 48px)',
  },
})

type FlexPortionFooterProps = {
  id: RecipeUUID
  position?: string
  surchargeLabel?: string | null
  isNegativeSurcharge?: boolean
  isRecipeAvailable?: boolean
  loading?: boolean
  onRecipeDetailsClose?: () => void
  numPortions: number
  portions?: Recipe['portions']
  onUpdatePortions: (numPortions: number) => void
  mode?: 'summary' | 'editor'
  onEditPortions?: () => void
  onCloseEditor?: () => void
}

export const FlexPortionFooter = ({
  id,
  position = '',
  surchargeLabel,
  isNegativeSurcharge,
  isRecipeAvailable,
  loading = false,
  onRecipeDetailsClose,
  numPortions: selectedNumPortions,
  portions,
  onUpdatePortions,
  mode,
  onEditPortions,
  onCloseEditor,
}: FlexPortionFooterProps) => {
  const menuUuid = useSelector(getBasketMenuUuid)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { getQuantitiesForRecipeId, recipeCount, removeRecipeByUuid } = useBasket()
  const recipeQuantity = getQuantitiesForRecipeId(id)
  const shouldShowFlexTreatment = recipeQuantity > 0 && isAuthenticated

  const flexPortionTrackingContext: FlexPortionTrackingContext = {
    basketRecipeCount: recipeCount,
    menuUuid,
    recipePortionCount: selectedNumPortions,
    recipeUuid: id,
    source: 'view_recipe_detail_screen',
  }

  useEffect(() => {
    if (!shouldShowFlexTreatment) {
      onCloseEditor?.()
    }
  }, [onCloseEditor, shouldShowFlexTreatment])

  if (!shouldShowFlexTreatment) {
    return (
      <Box width="100%">
        <AddRecipeButton
          id={id}
          surchargeLabel={surchargeLabel ?? undefined}
          isNegativeSurcharge={isNegativeSurcharge}
          isRecipeAvailable={isRecipeAvailable}
          loading={loading}
          position={position}
          onRecipeDetailsClose={onRecipeDetailsClose}
        />
      </Box>
    )
  }

  const handleRemoveRecipe = () => {
    removeRecipeByUuid(id, 'detail', position)
  }

  const handleEditPortions = () => {
    trackClickEditRecipeQuantity(flexPortionTrackingContext)
    onEditPortions?.()
  }

  const handleSelectPortions = (numPortions: number) => {
    trackSelectPortionCount(flexPortionTrackingContext, numPortions)
  }

  const handleUpdatePortions = (numPortions: number) => {
    onUpdatePortions(numPortions)
    trackUpdatePortion(flexPortionTrackingContext, numPortions)
  }

  return (
    <ModalFooterContent data-testid="detail-flex-portion-footer">
      <SharedFlexPortionFooter
        recipeCount={recipeQuantity}
        numPortions={selectedNumPortions}
        portions={portions}
        onSelectPortions={handleSelectPortions}
        onUpdatePortions={handleUpdatePortions}
        onRemoveRecipe={handleRemoveRecipe}
        mode={mode}
        onEditPortions={onEditPortions ? handleEditPortions : undefined}
        onCloseEditor={onCloseEditor}
      />
    </ModalFooterContent>
  )
}
